import React, { useCallback, useRef } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { ReactLink } from '../Link/Link'
import { Heading } from '../Heading/Heading'
import { apply } from '../../features/ContainerQuery'
import useResizeObserver from '../../features/useResizeObserver'
import { Button } from '../Button/Button'

/**
 * Bronson ItemTeaser component.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-item-teaser.html
 *
 * @param {boolean} [alternative] - Render alternative variant with more spacing and background color.
 * @param {boolean} [block] - Render the {@link icon} above the content.
 * @param {boolean} [blockSmallScreen] - Responsive switch to {@link block} for small viewports.
 * @param {React.ReactNode} [children] - The content of the component.
 * @param {string} [className] - Additional CSS classes on the component element.
 * @param {number|string} [headingLevel=5] - Specifies the heading element level, e.g. `5` => `<h5>`.
 * @param {string} [icon] - Render optional icon.
 * @param {string} [iconClassName] - Additional CSS classes on the icon element.
 * @param {string} [iconDescription] - Visually hidden description of the icon.
 * @param {boolean} [inline] - Render the {@link icon} aside the content.
 * @param {boolean} [inlineSmallScreen] - Responsive switch to {@link inline} for small viewports.
 * @param {boolean} [linkButton] - Render the link as a {@link Button}.
 * @param {string} [linkIcon='semantic-forward'] - Optional link icon, default to 'semantic-forward'.
 * @param {string} [linkLabel] - The link text content.
 * @param {Link.propTypes} [linkProps] - Additional props passed to the link element.
 * @param {boolean} [row] - Render variant where children will be displayed in a row if space is sufficient.
 * @param {boolean} [small] - Render smaller variant with decreased icon size and spacings.
 * @param {string} [title] - The `[title]` attribute.
 * @param {string} [testId] - The `[data-testid]` attribute on the component element.
 * @param {object} [otherProps] - Other props passed to the component element.
 * @return {JSX.Element} - The ItemTeaser component.
 * @constructor
 */
export function ItemTeaser({
  alternative,
  block,
  blockSmallScreen,
  children,
  className,
  headingLevel = 5,
  icon,
  iconClassName,
  iconDescription,
  inline,
  inlineSmallScreen,
  linkButton,
  linkIcon = 'semantic-forward',
  linkLabel,
  linkProps,
  row,
  small,
  title,
  testId,
  ...otherProps
}) {
  const ref = useRef(null)
  const applyQC = useCallback(apply, [apply])
  /**
   * Apply `onResizeHandler` handler.
   */
  useResizeObserver({
    ref,
    onResizeHandler: applyQC,
  })

  const articleClassNameList = classNames(
    'c-item-teaser',
    {
      // Convenience props from Bronson variants.
      'c-item-teaser--small': small,
      'c-item-teaser--inline': inline,
      'c-item-teaser--alternative': alternative,
      'c-item-teaser--row': row,
      'c-item-teaser--block': block,
      'c-item-teaser--inline@small-screen': inlineSmallScreen,
      'c-item-teaser--block@small-screen': blockSmallScreen,
    },
    className
  ).trim()

  const iClassNameList = classNames(
    'c-item-teaser__icon',
    'c-icon',
    { [`c-icon--[${icon}]`]: icon },
    iconClassName
  ).trim()

  function renderIfItemTeaserLinkLabel() {
    if (linkLabel) {
      const linkAttrs = linkButton ? {} : { link: true, simple: true, small: true }
      return (
        <div className="c-item-teaser__link">
          <Button icon={linkIcon} {...linkAttrs} {...linkProps}>
            {linkLabel}
          </Button>
        </div>
      )
    }
    return null
  }

  function renderIfIcon() {
    if (icon) {
      return (
        <>
          <i className={iClassNameList} aria-hidden="true" role="img" />
          <span className="c-item-teaser__icon-description u-visually-hidden">{iconDescription}</span>
        </>
      )
    }
    return null
  }

  function renderIfTitle() {
    if (title) {
      return (
        <Heading level={headingLevel} className="c-item-teaser__title">
          {title}
        </Heading>
      )
    }
    return null
  }

  function renderIfText() {
    if (children) {
      return <div className="c-item-teaser__text">{children}</div>
    }
    return null
  }

  return (
    <article {...otherProps} data-testid={testId} className={articleClassNameList} ref={ref}>
      {renderIfIcon()}
      <div className="c-item-teaser__body">
        {renderIfTitle()}
        {renderIfText()}
        {renderIfItemTeaserLinkLabel()}
      </div>
    </article>
  )
}

ItemTeaser.propTypes = {
  children: PropTypes.node, // Bronson template: 'item-teaser-text'.
  className: PropTypes.string, // Additional CSS classes on the component element. Bronson template: 'item-teaser-modifier'.
  headingLevel: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), // Bronson template: 'item-teaser-title-heading-level'.
  icon: PropTypes.string, // Bronson template: 'item-teaser-icon'.
  iconClassName: PropTypes.string, // Additional CSS classes on the icon element. Bronson template: 'item-teaser-icon'.
  iconDescription: PropTypes.string, // Bronson template: 'item-teaser-icon-description'.
  linkButton: PropTypes.bool, // Bronson template: 'item-teaser-link-button'.
  linkIcon: PropTypes.string, // Bronson template: 'item-teaser-link-icon'.
  linkLabel: PropTypes.string, // Bronson template: 'item-teaser-link-label'.
  linkProps: PropTypes.shape(ReactLink.propTypes),
  title: PropTypes.string, // Bronson template: 'item-teaser-title'.
  testId: PropTypes.string, // Added for the `[data-testid]` attribute.
  /* Convenience props. */
  alternative: PropTypes.bool, // Convenience prop for `.c-item-teaser--alternative`. Bronson template: 'item-teaser-modifier'.
  block: PropTypes.bool, // Convenience prop for `.c-item-teaser--block`. Bronson template: 'item-teaser-modifier'.
  blockSmallScreen: PropTypes.bool, // Convenience prop for `.c-item-teaser--block@small-screen`. Bronson template: 'item-teaser-modifier'.
  inline: PropTypes.bool, // Convenience prop for `.c-item-teaser--inline`. Bronson template: 'item-teaser-modifier'.
  inlineSmallScreen: PropTypes.bool, // Convenience prop for `.c-item-teaser--inline@small-screen`. Bronson template: 'item-teaser-modifier'.
  row: PropTypes.bool, // Convenience prop for `.c-item-teaser--row`. Bronson template: 'item-teaser-modifier'.
  small: PropTypes.bool, // Convenience prop for `.c-item-teaser--small`. Bronson template: 'item-teaser-modifier'.
}
